import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseInput from 'components/atoms/base-input';
import BaseTextArea from 'components/atoms/base-textarea';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import BaseGrid from 'components/atoms/base-grid';
import { type FC, useState, useEffect } from 'react';
import BaseDatepicker from 'components/atoms/base-datepicker';
import MedicationAutocomplete from 'components/molecules/medication-autocomplate';
import BaseButton from 'components/atoms/baseButton';
import MasterModal from 'components/organisms/masterModal';
import BaseSearchSelectWithOther from 'components/atoms/base-search-select-with-other';
import { Tag } from 'antd';
import classNames from 'classnames';

import { medicationValidationSchema } from './validation-schema';
import s from './styles.module.scss';
import { UNIT_OPTIONS, ROUTE_OPTIONS, FREQUENCY_OPTIONS } from './constants';

import type { IMedicationForUpdate } from 'infrastructure/interfaces';
import type { FormikHelpers } from 'formik';
import type { Values } from './validation-schema';

interface AddMedicationFormProps {
  onCancelForm: () => void;
  onSubmit: (values: Values, formikHelpers: FormikHelpers<Values>) => void;
  loading: boolean;
  modalTitle: string;
  open: boolean;
  defaultValues?: IMedicationForUpdate;
}

const AddMedicationForm: FC<AddMedicationFormProps> = ({
  onCancelForm,
  onSubmit,
  loading,
  defaultValues,
  modalTitle,
  open,
}) => {
  const { t } = useTranslation();
  const formik = useFormik<Values>({
    initialValues: medicationValidationSchema().cast(defaultValues),
    enableReinitialize: true,
    onSubmit,
    validateOnChange: false,
    validationSchema: medicationValidationSchema(),
  });

  const { submitForm, resetForm } = formik;
  const [otherValues, setOtherValues] = useState<{
    unit?: string;
    route?: string;
    frequency?: string;
  }>({});

  useEffect(() => {
    if (!defaultValues) return;

    const newOtherValues: typeof otherValues = {};
    if (defaultValues.unit) {
      const unitExists = UNIT_OPTIONS.some(
        (opt) => opt.value === defaultValues.unit,
      );
      if (!unitExists) {
        newOtherValues.unit = defaultValues.unit;
      }
    }
    if (defaultValues.route) {
      const routeExists = ROUTE_OPTIONS.some(
        (opt) => opt.value === defaultValues.route,
      );
      if (!routeExists) {
        newOtherValues.route = defaultValues.route;
      }
    }

    if (defaultValues.frequency) {
      const frequencyExists = FREQUENCY_OPTIONS.some(
        (opt) => opt.value === defaultValues.frequency,
      );
      if (!frequencyExists) {
        newOtherValues.frequency = defaultValues.frequency;
      }
    }

    setOtherValues(newOtherValues);
  }, [defaultValues]);

  const handleDosageChange = (value: string) => {
    const sanitized = value
      .replace(',', '.')
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');

    formik.setFieldValue('dosage', sanitized);
  };

  const onCancel = () => {
    resetForm();
    setOtherValues({});
    onCancelForm();
  };

  const isStatusActive = formik.values.isActive;
  const statusLabel = isStatusActive
    ? t('labels.active')
    : t('labels.inactive');

  return (
    <>
      {open && (
        <MasterModal
          title={modalTitle}
          isOpen={open}
          toggle={onCancel}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          footer={
            <>
              <BaseButton
                dataCy="cancel-button"
                key="cancel"
                type="secondary"
                label={t('controls.cancel')}
                onClick={onCancel}
              />
              <BaseButton
                dataCy="save-button"
                key="save"
                type="primary"
                label={defaultValues ? t('controls.save') : t('controls.add')}
                loading={loading}
                onClick={submitForm}
              />
            </>
          }
        >
          <BaseForm
            formik={formik}
            withControls
            plaintext={false}
            readonly={false}
            loading={loading}
          >
            <>
              <div className={s['medication-row']}>
                <BaseFormItem
                  name="name"
                  label={`${t('labels.medicationName')}`}
                  required
                  className={classNames(
                    s['medication-field'],
                    s['custom-label-size'],
                  )}
                >
                  <MedicationAutocomplete
                    disabled={typeof defaultValues !== 'undefined'}
                  />
                </BaseFormItem>
                <BaseFormItem
                  name=""
                  label={`${t('labels.status')}`}
                  className={s['custom-label-size']}
                >
                  <Tag
                    color={statusLabel ? 'success' : 'default'}
                    data-cy="medication-status"
                    className={s['status-tag']}
                  >
                    {statusLabel ? t('labels.active') : t('labels.inactive')}
                  </Tag>
                </BaseFormItem>
              </div>

              <div className={s.divider} />

              <div className={s.section}>
                <h3 className={s['section-title']}>
                  {t('labels.dosage').toUpperCase()}
                </h3>
                <BaseGrid
                  columns={2}
                  columnGap={24}
                  rowGap={0}
                  className={s['inputs-wrapper']}
                >
                  <BaseFormItem
                    name="dosage"
                    label={t('labels.amount')}
                    className={s['custom-label-size']}
                  >
                    <BaseInput
                      onChange={handleDosageChange}
                      placeholder="e.g. 500"
                    />
                  </BaseFormItem>
                  <BaseFormItem
                    name="unit"
                    label={t('labels.unit')}
                    className={s['custom-label-size']}
                  >
                    <BaseSearchSelectWithOther
                      options={UNIT_OPTIONS as any}
                      value={formik.values.unit ?? undefined}
                      otherValue={otherValues.unit}
                      onOtherChange={(v) =>
                        setOtherValues((prev) => ({ ...prev, unit: v }))
                      }
                      onSelectValue={(v) => formik.setFieldValue('unit', v)}
                      placeholder={t('labels.selectUnit')}
                      disabled={loading}
                      isLoading={false}
                    />
                  </BaseFormItem>
                  <BaseFormItem
                    name="route"
                    label={t('labels.route')}
                    className={s['custom-label-size']}
                  >
                    <BaseSearchSelectWithOther
                      options={ROUTE_OPTIONS as any}
                      value={formik.values.route ?? undefined}
                      otherValue={otherValues.route}
                      onOtherChange={(v) =>
                        setOtherValues((prev) => ({ ...prev, route: v }))
                      }
                      onSelectValue={(v) => formik.setFieldValue('route', v)}
                      placeholder={t('labels.selectRoute')}
                      disabled={loading}
                      isLoading={false}
                    />
                  </BaseFormItem>
                  <BaseFormItem
                    name="frequency"
                    label={t('labels.frequency')}
                    className={s['custom-label-size']}
                  >
                    <BaseSearchSelectWithOther
                      options={FREQUENCY_OPTIONS as any}
                      value={formik.values.frequency ?? undefined}
                      otherValue={otherValues.frequency}
                      onOtherChange={(v) =>
                        setOtherValues((prev) => ({ ...prev, frequency: v }))
                      }
                      onSelectValue={(v) =>
                        formik.setFieldValue('frequency', v)
                      }
                      placeholder={t('labels.selectFrequency')}
                      disabled={loading}
                      isLoading={false}
                    />
                  </BaseFormItem>
                </BaseGrid>
              </div>

              <div className={s.divider} />

              <div className={s.section}>
                <h3 className={s['section-title']}>
                  {t('labels.duration').toUpperCase()}
                </h3>
                <BaseGrid
                  columns={2}
                  columnGap={24}
                  className={s['inputs-wrapper']}
                >
                  <BaseFormItem
                    name="startDate"
                    label={t('labels.startDate')}
                    className={s['custom-label-size']}
                  >
                    <BaseDatepicker
                      placeholder={t('labels.selectDate')}
                      disabled={loading}
                    />
                  </BaseFormItem>
                  <BaseFormItem
                    name="endDate"
                    label={t('labels.endDate')}
                    className={s['custom-label-size']}
                  >
                    <BaseDatepicker
                      placeholder={t('labels.selectDate')}
                      disabled={loading}
                    />
                  </BaseFormItem>
                </BaseGrid>
              </div>

              <div className={s.divider} />

              <div className={s.section}>
                <BaseFormItem
                  name="note"
                  label={t('labels.note')}
                  className={s['custom-label-size']}
                >
                  <BaseTextArea
                    rows={2}
                    placeholder={t('labels.typeYourNoteHere')}
                    disabled={loading}
                  />
                </BaseFormItem>
              </div>
            </>
          </BaseForm>
        </MasterModal>
      )}
    </>
  );
};

export default AddMedicationForm;
